// Passing paramok → CabinetFrame "Relations" style-script transzform (KIZÁRÓLAG Unity-kimenet).
//
// V36 óta a railing-tab MINDHÁROM paramja ide fut be: a BackPassing (back/byRelation alak) MELLETT
// a Top/BottomPassingRelation Enum-ok is (entry alak), és a mergeRelationsWrappers az azonos célra
// (TargetType+Path) mutató Relations-wrappereket EGYETLEN wrapperbe fésüli — a Unity-spec szerinti
//   { "TargetType":"CabinetFrame", "Relations":[ Top, Bottom, Back ] }
// formára (a bemeneti wrapperek sorrendje — és így a Relations-entryk sorrendje is — a hívó fél
// build-tier rendezéséből adódik, lásd ParametersProcessor.generateStyleScript + buildOrder.ts).
//
// ADAT-VEZÉRELT: a Relations-template a param DB-descriptorából jön (style_parameter_type_descriptor
// `relations` kulcsa), NEM a kódból. Így a kódban NINCS beégetett üzleti érték, és a feature
// magától a KONYHAI CABINETEKRE szűkül: csak azok a termékek kapnak Relations-t, amelyek
// descriptorában van `relations` (a migráció ezt referencia szerint teszi hozzá). Descriptor nélkül
// a param változatlanul megy tovább (nem-konyhai termékek érintetlenek).
//
// A DB, a BackPassingCardContent kártya és a kosár-descriptor (generateParameterScripts) a
// BackPassing formát tartja: { Relation, Depth, Margin, Top } — ez round-trippel a DB paramhoz és
// a kártyához, a szerver-árazás is ezt kapja. A transzformot CSAK a Unity stylescript-generálásban
// alkalmazzuk (ParametersProcessor.generateStyleScript).
//
// Descriptor-alak (relations) — kétféle, a param értéke szerint:
//   BackPassing (objektum-érték {Relation,Depth,Margin,Top}) → `back`, a Back entry a user
//   relation-választásától függ:
//     "back": {
//       "Target":"Back", "Joining":"All",
//       "byRelation": {
//         "Butt":   { "Relation":"Butt" },
//         "Inlay":  { "Relation":"InLay", "TargetMargin":"@Depth" },
//         "Groove": { "Relation":"InLay", "TargetMargin":"@Depth", "TargetDepth":"@Margin" }
//       }
//     }
//   Passing Enum (skalár érték, pl. Top/BottomPassingRelation) → `entry`, az enum-értékből képez egyet:
//     "entry": {
//       "Target":"Top", "Joining":"Horizontal",
//       "byRelation": { "Butt":{"Relation":"Butt"}, "Inlay":{"Relation":"InLay"} }
//     }
// Placeholder-konvenció a numerikus mezőkben: "@Depth" / "@Margin" → a BackPassing érték adott
// mezőjének mm-értéke; sima szám ("9") vagy "9mm" → fix érték. Így fix ÉS dinamikus is megadható
// a descriptorból, kód-módosítás nélkül.
// A "@Param.Property" alak (pont van benne) egy MÁSIK paraméterre mutat — skalár értékű paramokhoz,
// ahol a ráhagyást külön Size param tárolja. A kimenet itt is szám, mint a saját objektumra mutatónál.
// Bármelyik "@"-hivatkozás elé írható "-": ilyenkor az érték NEGÁLVA megy ki ("-@Depth"). A UI-n és a
// tárolásban az érték pozitív marad — az előjel a Unity-szerződés része, nem az értéké.

import DimensionUtils from "../../common/DimensionUtils";
import { isParamVisible, TVisibleWhen } from "../../parametersProcessor/paramVisibility";
import TParameters from "../../parametersProcessor/TParameters";

/**
 * Egy Relations-entry. A nevesített mezők a Unity-spec szerintiek, az index-signature pedig azért kell,
 * mert a KULCS-SORREND adatból jön (lásd TRelationEntry) — a kimenet kulcsai a template sorrendjét
 * követik, nem egy kódban fixált mezőlistát.
 */
export interface IRelation {
    [field: string]: string | number | boolean | object | undefined;
    Target: string;
    Relation: string;
    Joining: string;
    TargetMargin?: number;
    TargetDepth?: number;
    Top?: boolean;
}

/**
 * Egy entry template-je KÉTFÉLE alakban adható meg:
 *
 *  1) SORREND-TARTÓ (ajánlott): egy-kulcsú objektumok TÖMBJE, ahol a tömb sorrendje = a kimeneti JSON
 *     kulcs-sorrendje:
 *       [{"Target":"Back"},{"Relation":"InLay"},{"Joining":"All"},{"TargetMargin":"@Depth"}]
 *     Miért így: a descriptor MySQL-en `json` típusú oszlopban él, ami az OBJEKTUM kulcsait átrendezi
 *     (hossz, majd ábécé szerint) — a TÖMB elemeinek sorrendjét viszont megtartja. Ha a Unity-nak menő
 *     kulcs-sorrend kötött, csak így garantálható.
 *
 *  2) SIMA OBJEKTUM (a korábbi alak, változatlanul támogatott): {"Relation":"InLay","TargetMargin":"@Depth"}
 *     — ilyenkor a kimenet Target, Relation, Joining, majd a többi mező sorrendben áll össze, a hiányzó
 *     Target/Joining pedig a `back` szintjéről, a Relation a kötés-típusból jön.
 */
export type TRelationEntry =
    | Array<{ [field: string]: string | number }>
    | { [field: string]: string | number };

/**
 * Egy kötés-típus szabálya HÁROM alakban:
 *   - `byTop`   : a "Tető is érintett" csúszka állása szerinti entry-LISTA ("true" / "false" kulcs).
 *                 Bekapcsolva a hátlap a tetőhöz illeszkedik (1 entry), kikapcsolva a hátlap a tető ELÉ
 *                 jön, és ezt három entry írja le.
 *   - `entries` : fix entry-lista, a csúszkától függetlenül.
 *   - maga a szabály-objektum = EGYETLEN entry (a korábbi alak; így a nem migrált descriptorok és a
 *     passing Enumok viselkedése nem változik).
 */
export type TRelationRule =
    | { byTop: { [topState: string]: TRelationEntry[] } }
    | { entries: TRelationEntry[] }
    | { [field: string]: string | number };

export interface IRelationsDescriptor {
    back?: {
        Target?: string;
        Joining?: string;
        byRelation?: { [relation: string]: TRelationRule };
        /**
         * @TODO: Will be removed later [EA - 748]
         * ASZIMMETRIKUS HÁTLAP: a "Látható oldal" választás (az érték `VisibleSide` mezője) → kötés-
         * IRÁNYOK. A látható oldal falcolva, a többi ráfekvő — ezért a Back entry KÉT entryre válik, és
         * a kettő `Joining`-ja innen jön, `@Visible` / `@Hidden` placeholderrel a template-ekben:
         *   "byVisibleSide": { "None":  {"Hidden":"All",              "Visible":""},
         *                      "Right": {"Hidden":"Left,Bottom,Top",  "Visible":"Right"} }
         * Az oldalnevek és a komplementerük így végig ADATOK — a kódban egyetlen irány-név sincs.
         */
        byVisibleSide?: { [side: string]: { [field: string]: string } };
        /**
         * ÚTVONALANKÉNTI scope: a wrapper `Path`-je (pl. az `emitPaths` egyik útvonala) → a `@`-placeholderek
         * értékei. Így EGY érték több útvonalon eltérő `Joining`-gal mehet ki (szekrénysor: a bal szélső
         * keret `Left`, a jobb szélső `Right` oldala a látható):
         *   "byPath": { "…HorizontalStackZone[0].CabinetFrame": {"Visible":"Left"}, … }
         */
        byPath?: { [path: string]: { [field: string]: string } };
    };
    // Skalár (Enum) passing-paramok template-je (pl. TopPassingRelation / BottomPassingRelation):
    // a param ÉRTÉKE (egy enum-kulcs, pl. "Butt" | "Inlay") választja ki a byRelation-szabályt.
    //   { "entry": { "Target":"Top", "Joining":"Horizontal",
    //                "byRelation": { "Butt":{"Relation":"Butt"}, "Inlay":{"Relation":"InLay"} } } }
    // Így az enum-érték → Unity Relation leképezés (pl. Inlay → InLay) is ADAT, nem kód.
    entry?: {
        Target?: string;
        Joining?: string;
        byRelation?: { [relation: string]: { [field: string]: TEntryRuleField } };
    };
}

/** A generateStyleScript-nek átadott, paraméter-névhez kötött relations-infó. */
export interface IRelationsParamInfo {
    /** A wrapper property-kulcsa (a végleges, indexelt forma, pl. "BackPassing" / "TopPassingRelation"). */
    property: string;
    descriptor: IRelationsDescriptor;
    /** A BackPassing `top.offWhen`-je: ha teljesül, a Top kikapcsoltként oldódik fel. */
    topOffWhen?: TVisibleWhen;
}

/** "9mm" | 9 | "9" → 9 (számérték). Érvénytelennél 0. */
const mm = (v: any): number => {
    const d = DimensionUtils.parseDimension(String(v ?? ""));
    return isNaN(d.num) ? 0 : d.num;
};

/**
 * Egy descriptor-mezőérték feloldása számmá:
 *   number               → változatlanul
 *   "@Depth"/"@Margin"   → value[Depth|Margin] mm-értéke (a SAJÁT objektum-érték mezője),
 *   "9mm" / "9"          → fix mm
 */
const resolveNum = (spec: string | number, value: Record<string, any>): number => {
    if (typeof spec === "number") return spec;
    const { sign, ref } = signedRef(String(spec));
    return ref.startsWith("@") ? sign * mm(value[ref.slice(1)]) : mm(ref);
};

/**
 * ELŐJELES HIVATKOZÁS: a `@`-placeholder elé írt `-` azt jelenti, hogy az érték NEGÁLVA megy ki
 * (`"-@Depth"` → a Ráhagyás mínusz egyszerese). A UI-n és a tárolásban az érték pozitív marad; az
 * előjel a Unity-szerződés része, nem az értéké — ugyanaz az elv, mint az `emitNumber`-nél.
 *
 * Minden `@`-alakra érvényes, a saját objektumra mutatóra és a `@Param.Property` keresztre is, tehát
 * nem kell hozzá mezőnév a kódban.
 */
const signedRef = (spec: string): { sign: number; ref: string } =>
    spec.startsWith("-@") ? { sign: -1, ref: spec.slice(1) } : { sign: 1, ref: spec };

/** "@Param.Property" → [paraméter, property, előjel], vagy null, ha a spec nem ilyen alakú. */
const crossRef = (spec: string | number): [string, string, number] | null => {
    if (typeof spec !== "string") return null;
    const { sign, ref } = signedRef(spec);
    if (!ref.startsWith("@")) return null;
    const dot = ref.indexOf(".");
    return dot < 0 ? null : [ref.slice(1, dot), ref.slice(dot + 1), sign];
};

/**
 * Egy descriptor target-mező feloldása a kimeneti értékére:
 *   "@Top"            → a BackPassing érték Top mezője booleanként (a value "true"/"false"-t tárol),
 *   "@Param.Property" → egy MÁSIK paraméter mezője, mm-számként. Skalár értékű paramokhoz való: a
 *                       Top/Bottom kötés Enum-ja mellé külön Size param adja a ráhagyást,
 *   "-@…"             → bármelyik fenti hivatkozás NEGÁLVA (lásd signedRef),
 *   egyéb             → resolveNum (numerikus, placeholderrel vagy fix mm-mel).
 * MINDEN numerikus mező SZÁMKÉNT megy ki, a `TargetMargin`/`TargetDepth` bizonyítottan működő alakja
 * szerint — a Unity `PassingRelations` style-eleme ezt a két numerikus mezőt ismeri (a build
 * metaadatában `set_TargetMargin` / `set_TargetDepth`; egységgel megadott érték nem értelmezhető).
 */
const resolveField = (
    spec: string | number,
    value: Record<string, any>,
    params?: Record<string, any>
): number | boolean => {
    if (spec === "@Top") return value.Top === true || value.Top === "true";

    const ref = crossRef(spec);
    return ref ? ref[2] * mm(params?.[ref[0]]?.[ref[1]]) : resolveNum(spec, value);
};

/** A kulcs-sorrendet ADÓ mező-lista egy entry template-jéből (lásd TRelationEntry). */
const entryFieldPairs = (
    template: TRelationEntry,
    fallback: { Target?: string; Joining?: string },
    relation: string
): Array<[string, string | number]> => {
    // 1) Sorrend-tartó alak: a tömb sorrendje a kimenet kulcs-sorrendje, a template mindent felsorol.
    if (Array.isArray(template)) {
        return template.flatMap((field) => Object.entries(field) as Array<[string, string | number]>);
    }
    // 2) Sima objektum (korábbi alak): Target, Relation, Joining, majd a többi mező — a hiányzókat a
    //    `back` szintjéről, ill. a kötés-típusból pótoljuk (a régi viselkedés változatlan).
    const rest = Object.entries(template).filter(
        ([key]) => key !== "Target" && key !== "Relation" && key !== "Joining"
    ) as Array<[string, string | number]>;
    return [
        ["Target", template.Target ?? fallback.Target ?? "Back"],
        ["Relation", template.Relation ?? relation],
        ["Joining", template.Joining ?? fallback.Joining ?? "All"],
        ...rest,
    ];
};

/** A NEM numerikus (azonosító) mezők: ezek a Unity-entry azonosító mezői, nem target-értékek. */
const LITERAL_ENTRY_FIELDS = ["Target", "Relation", "Joining"];

/**
 * Azonosító mező feloldása: a "@Mező" alak az ÉRTÉK adott mezőjét adja vissza SZÖVEGKÉNT (a numerikus
 * mezőknél a resolveField teszi ugyanezt számmal). Így a `Joining` iránylistája is jöhet a felhasználó
 * választásából (lásd `byVisibleSide`), kódba írt oldalnév nélkül. Minden más spec változatlan.
 */
const resolveLiteral = (spec: string | number, value: Record<string, any>): string => {
    const s = String(spec);
    return s.startsWith("@") ? String(value[s.slice(1)] ?? "") : s;
};

/**
 * BackPassing érték { Relation, Depth, Margin, Top } + `back` descriptor → CabinetFrame Relations-tömb.
 * Üres tömb, ha nincs `back` vagy nincs szabály a választott kötésre.
 *
 * Egy kötés EGY vagy TÖBB entryt is leírhat (lásd TRelationRule): a `byTop` a "Tető is érintett" csúszka
 * állása szerint választ entry-listát, az `entries` fix listát ad, a régi egy-objektumos alak pedig
 * továbbra is egyetlen entryt jelent. A kimenet KULCS-SORRENDJE a template-ből jön (TRelationEntry).
 */
export function resolveRelations(value: any, desc: IRelationsDescriptor, path?: string): IRelation[] {
    const v = (value && typeof value === "object") ? value : {};
    const relation = String(v.Relation ?? "");

    const back = desc.back;
    const rule = back?.byRelation?.[relation] as any;
    if (!back || !rule) return [];

    // Melyik entry-lista érvényes: byTop (a csúszka állása) → entries → maga a szabály (egy entry).
    const topOn = v.Top === true || v.Top === "true";
    const byTopList = rule.byTop?.[String(topOn)];
    const templates: TRelationEntry[] = Array.isArray(byTopList)
        ? byTopList
        : (Array.isArray(rule.entries) ? rule.entries : [rule as TRelationEntry]);

    // A "Látható oldal" választás kötés-IRÁNYOKRA oldva (`@Visible` / `@Hidden`). A mezők csak a
    // template-feloldás idejére léteznek — a tárolt értékbe nem kerülnek bele. `byVisibleSide` nélkül
    // a scope maga az érték, tehát a nem migrált descriptorok kimenete bitre a régi.
    //
    // Nincs eltárolt (vagy már nem létező) oldal-érték → az ELSŐ felsorolt eset, ugyanazzal a
    // konvencióval, amit a kártya is használ a kötésnél. Enélkül a `@Hidden` üresre oldódna, és a
    // MAI, `VisibleSide` nélküli értékek üres Relations-tömböt adnának.
    const sides = back.byVisibleSide;
    const sideKeys = sides ? Object.keys(sides) : [];
    const scope = {
        ...v,
        ...(sides?.[String(v.VisibleSide ?? "")] ?? sides?.[sideKeys[0]] ?? {}),
        ...(back.byPath?.[path ?? ""] ?? {}),
    };

    return templates
        .map((template) => {
            const entry = {} as IRelation;
            for (const [key, spec] of entryFieldPairs(template, back, relation)) {
                entry[key] = LITERAL_ENTRY_FIELDS.includes(key)
                    ? resolveLiteral(spec, scope)
                    : resolveField(spec, scope);
            }
            return entry;
        })
        // Üres iránylistájú entry nem megy ki: a szimmetrikus eset (nincs látható oldal) így külön ág
        // nélkül adja vissza a mai, egy-entrys kimenetet.
        .filter((entry) => entry.Joining !== "");
}

/**
 * Egy `byRelation`-szabály mezője. A skalár a régi alak (méret vagy "@Param.Property" hivatkozás);
 * a `byValue` ugyanazt az idiómát viszi egy szinttel lejjebb, amit a `byRelation` maga is használ:
 * egy MÁSIK paraméter ÉRTÉKE választja ki, melyik objektum kerüljön a kötés-entrybe.
 *
 *   "JointHardware": { "paramName":"CabinetFrameJoineryType", "property":"JoineryType",
 *                      "byValue": { "Minifix": { … }, "Dowel": { … } } }
 *
 * Nincs találat (vagy a választott értékhez nincs bejegyzés) → a mező KIMARAD az entryből. Így a
 * "nincs vasalat" eset külön jelölő nélkül leírható: egyszerűen nem szerepel a `byValue`-ban.
 */
export type TEntryRuleField = string | number | {
    paramName: string;
    property: string;
    byValue: { [value: string]: object };
};

/** A `byValue`-alakú mező feloldása az aktuális paraméter-állapotból; null, ha nincs találat. */
const resolveByValue = (
    spec: TEntryRuleField,
    params?: Record<string, any>
): object | null => {
    if (!spec || typeof spec !== "object" || !("byValue" in spec)) return null;
    const current = params?.[spec.paramName]?.[spec.property];
    return spec.byValue[String(current ?? "")] ?? null;
};

/** Skalár (Enum) passing-érték + `entry` descriptor → egyetlen Relations-entry (vagy null, ha
 *  nincs az értékhez szabály — ilyenkor a hívó a wrappert VÁLTOZATLANUL engedi tovább). */
export function resolveEntryRelation(
    value: any,
    entry: NonNullable<IRelationsDescriptor["entry"]>,
    params?: Record<string, any>
): IRelation | null {
    const rule = entry.byRelation?.[String(value ?? "")];
    if (!rule) return null;
    const rel: IRelation = {
        Target: entry.Target ?? "",
        Relation: String(rule.Relation ?? value),
        Joining: entry.Joining ?? "All",
    };
    // Extra mezők (ha a szabály tartalmaz ilyet). Skalár értéknél nincs SAJÁT "@mező" forrás, de a
    // "@Param.Property" alak egy MÁSIK paraméterből olvas — így a kötéshez tartozó ráhagyás ugyanabba
    // az entrybe kerül, ahol a Target/Joining azonosítja, hogy melyik kötésről van szó.
    for (const key of Object.keys(rule)) {
        if (key === "Relation") continue;
        const spec = rule[key];
        // Objektum-értékű mező (byValue): a hivatkozott paraméter értéke választja ki. Találat
        // híján a mező kimarad — nem kerül `null` az entrybe, amit a Unity nem tudna értelmezni.
        if (spec && typeof spec === "object") {
            const resolved = resolveByValue(spec, params);
            if (resolved !== null) (rel as any)[key] = resolved;
            continue;
        }
        (rel as any)[key] = resolveField(spec, {}, params);
    }
    return rel;
}

/**
 * ÁLTALÁNOS wrapper → Relations transzform. Az infó a paramhoz kötött property-kulcsot ÉS a
 * descriptort hozza; a descriptor alakja dönt:
 *   - back  (BackPassing objektum-érték) → resolveRelations,
 *   - entry (skalár Enum-érték)          → resolveEntryRelation.
 * Descriptor vagy kulcs hiányában, ill. ismeretlen enum-értéknél a wrapper VÁLTOZATLAN marad
 * (nem-konyhai termékek / régi adatok érintetlenek — nincs regresszió).
 */
export function mapRelationsWrapper(
    param: Record<string, any>,
    info?: IRelationsParamInfo | null,
    params?: Record<string, any>
): Record<string, any> {
    if (!param || typeof param !== "object" || !info?.descriptor) return param;
    const key = info.property;
    if (!(key in param)) return param;
    const desc = info.descriptor;

    let relations: IRelation[] | null = null;
    if (desc.back) {
        const value = info.topOffWhen && isParamVisible(params as TParameters, info.topOffWhen)
            ? { ...param[key], Top: "false" }
            : param[key];
        relations = resolveRelations(value, desc, param.Path);
    } else if (desc.entry) {
        const rel = resolveEntryRelation(param[key], desc.entry, params);
        relations = rel ? [rel] : null;
    }
    if (!relations) return param;

    const out: Record<string, any> = {};
    for (const k of Object.keys(param)) {
        if (k === key) continue;
        out[k] = param[k];
    }
    out.Relations = relations;
    return out;
}

/**
 * Az AZONOS célra (TargetType + Path) mutató, Relations-t hordozó wrapperek összefésülése EGY
 * wrapperbe — a Unity a CabinetFrame-kötéseket EGYETLEN Relations-tömbként várja:
 *   { "TargetType":"CabinetFrame", "Relations":[ Top, Bottom, Back ] }
 * Az első előfordulás helyén marad, a Relations a wrapperek sorrendjében fűződik össze (a
 * paramScripts sort_index-rendje → Top, Bottom, Back). A Relations NÉLKÜLI wrapperekhez (más
 * paramtípusok, akár azonos targetre) NEM nyúlunk.
 */
export function mergeRelationsWrappers(wrappers: any[]): any[] {
    const out: any[] = [];
    const indexByGroup = new Map<string, number>();
    for (const w of wrappers) {
        if (w && typeof w === "object" && Array.isArray(w.Relations)) {
            const groupKey = `${w.TargetType ?? ""}|${w.Path ?? ""}`;
            const idx = indexByGroup.get(groupKey);
            if (idx !== undefined) {
                out[idx] = { ...out[idx], Relations: [...out[idx].Relations, ...w.Relations] };
                continue;
            }
            indexByGroup.set(groupKey, out.length);
        }
        out.push(w);
    }
    return out;
}
